import { useEffect, useState } from 'react'

/**
 * Registering the worker that makes the application usable offline.
 *
 * Only in a built application. During development a cached shell would serve
 * stale code and make changes look like they had not taken effect.
 */
export function registerServiceWorker(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return
  if (import.meta.env.DEV) {
    // A worker left on this origin by an earlier built or preview run would
    // keep answering with cached modules, so changes, or a module caught half
    // written, would survive every reload. Remove it and its caches.
    void navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) void registration.unregister()
    })
    if ('caches' in window) {
      void caches.keys().then((keys) => keys.forEach((key) => void caches.delete(key)))
    }
    return
  }

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register(`${import.meta.env.BASE_URL}sw.js`).catch(() => {
      // Nothing to do. Offline support is an enhancement, and the application
      // works exactly as before without it.
    })
  })
}

/** Whether the browser currently believes it has a network. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )

  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  return online
}
